import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, Clock, Info, ListChecks } from "lucide-react";
import RichBlock, { Unavailable } from "@/components/vetting/RichBlock";
import ResourceCard, { PrimaryZone, primaryFlags } from "@/components/vetting/ResourceCard";
import { Expandable } from "@/components/network/parts";

export const GUIDE = "/new-partner-guide";
const ROW = "group w-full flex items-center gap-3 min-h-[48px] px-3 py-2.5 rounded-lg border border-slate-200 bg-white text-left hover:border-[#C9A961] hover:bg-[#C9A961]/5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A961]";
const TAG = "text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500 bg-slate-100 rounded px-1.5 py-0.5 shrink-0";

function InApp({ to, label, tag, testid }) {
  return (
    <Link to={to} className={ROW} data-testid={testid}>
      <ArrowRight className="w-4 h-4 text-[#8A6D1F] shrink-0" />
      <span className="flex-1 min-w-0 text-sm font-medium text-[#0A1F44] break-words">{label}</span>
      <span className={TAG}>{tag}</span>
    </Link>
  );
}

function Item({ it, testid, primary }) {
  if (it.type === "resource") return <ResourceCard seg={it.seg} testid={testid} primary={primary} />;
  if (it.type === "route") return <InApp to={it.to} label={it.label} tag={it.to.startsWith("/talk-library") ? "Podcast Library" : "In app"} testid={testid} />;
  if (it.type === "section") return <InApp to={`${GUIDE}/${it.to}`} label={it.label} tag="Section" testid={testid} />;
  if (it.type === "soon") return (
    <div className="flex items-center gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-2.5 text-sm text-slate-500" data-testid={testid} aria-disabled="true">
      <Clock className="w-4 h-4 shrink-0" /><span className="flex-1">{it.text}</span><span className={TAG}>Coming soon</span>
    </div>
  );
  if (it.type === "group") return (
    <div className="pt-2 text-[10px] uppercase tracking-[0.2em] font-semibold text-[#0A1F44]" data-testid={testid}>{it.text}</div>
  );
  if (it.type === "note") return (
    <p className="flex items-start gap-2 text-sm text-slate-700" data-testid={testid}><Info className="w-4 h-4 mt-0.5 text-[#8A6D1F] shrink-0" />{it.text}</p>
  );
  return <Unavailable text={`${it.text} — link not provided`} testid={testid} />;
}

export default function Topic({ t, sid, highlight, open, onToggle, primary = false }) {
  const id = `np-${sid}-${t.id}`;
  const segs = t.resources.map((r) => (r.type === "resource" ? r.seg : {}));
  const flags = primaryFlags(segs, primary);
  return (
    <section id={t.id} data-testid={id} data-open={open ? "true" : "false"}
             className={`scroll-mt-24 rounded-2xl border bg-white transition-shadow ${highlight ? "border-[#C9A961] ring-2 ring-[#C9A961]/40" : "border-slate-200"}`}>
      <h3>
        <button type="button" onClick={onToggle} aria-expanded={open} aria-controls={`${id}-panel`} data-testid={`${id}-toggle`}
                className="w-full min-h-[52px] flex items-center gap-3 px-4 sm:px-5 py-3 text-left rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A961]">
          <span className="flex-1 text-[13px] uppercase tracking-[0.14em] text-[#0A1F44] font-semibold leading-snug">{t.label}</span>
          <ChevronDown aria-hidden="true" className={`w-5 h-5 text-[#0A1F44] shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </h3>
      <div id={`${id}-panel`} hidden={!open} className="px-4 sm:px-5 pb-4 sm:pb-5 space-y-4" data-testid={`${id}-panel`}>
      {t.body.length > 0 && <PrimaryZone on={primary}><RichBlock runs={t.body} testid={`${id}-body`} /></PrimaryZone>}
      {t.example && (
        <Expandable label="See the example" testid={`${id}-example`}><RichBlock runs={t.example} testid={`${id}-example-body`} /></Expandable>
      )}
      {t.resources.length > 0 && (
        <div className="space-y-1.5" data-testid={`${id}-resources`}>
          {t.resources.map((it, i) => <Item key={i} it={it} primary={flags[i]} testid={`${id}-res-${i}`} />)}
        </div>
      )}
      {t.action.length > 0 && (
        <div className="rounded-xl bg-[#0A1F44]/[0.04] border-l-4 border-[#C9A961] px-4 py-3 space-y-1" data-testid={`${id}-action`}>
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#0A1F44] font-semibold"><ListChecks className="w-3.5 h-3.5" /> What to do</div>
          <RichBlock runs={t.action} testid={`${id}-action-body`} />
        </div>
      )}
      </div>
    </section>
  );
}
